import React from "react";
import { useTranslation } from "react-i18next";
import RadioInput from "../inputs/radioInput";
import { useGuestCheckoutSettings } from "../context/GuestCheckoutSettingsContext";
import "../../styles/customer-details.scss";

export interface Offer {
  readonly _id: string;
  readonly title: string;
  readonly description?: string;
}

interface OffersSelectorProps {
  readonly offers: readonly Offer[];
  readonly selectedOffer: string | null;
  readonly onSelectOffer: (offerId: string | null) => void;
}

const NO_THANKS_VALUE = "";

const OffersSelector: React.FC<OffersSelectorProps> = ({ offers, selectedOffer, onSelectOffer }) => {
  const { t } = useTranslation();
  const { isWidget } = useGuestCheckoutSettings();

  if (offers.length === 0) {
    return null;
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    onSelectOffer(value === NO_THANKS_VALUE ? null : value);
  };

  return (
    <fieldset className="gcw:w-full gcw:border gcw:rounded-lg gcw:border-alert-info-border gcw:bg-alert-info gcw:p-4">
      <p className={isWidget ? "gcw:sr-only" : "gcw:block gcw:text-body gcw:font-semibold gcw:text-text-primary"}>
        {t("booking_offers_title", "Offers available")}
      </p>
      <p className="gcw:text-body gcw:mb-2">
        {t("booking_offers_question", "Would you like to add an offer to your booking?")}
      </p>
      <div className="gcw:flex gcw:flex-col">
        {offers.map((offer) => (
          <div key={offer._id} className="">
            <RadioInput
              id={`offer-${offer._id}`}
              name="offer"
              value={offer._id}
              checked={selectedOffer === offer._id}
              onChange={handleChange}
              labelClassName="gcw:flex gcw:items-start"
              label={
                <span className="gcw:text-body gcw:text-text-primary">
                  <span className="gcw:font-semibold">{offer.title}</span>
                  {offer.description && <span className="gcw:block gcw:text-body">{offer.description}</span>}
                </span>
              }
            />
          </div>
        ))}
        <div>
          <RadioInput
            id="offer-no-thanks"
            name="offer"
            value={NO_THANKS_VALUE}
            checked={selectedOffer === null}
            onChange={handleChange}
            label={
              <span className="gcw:text-body gcw:text-text-primary gcw:font-semibold">
                {t("booking_offers_no_thanks", "No thanks")}
              </span>
            }
          />
        </div>
      </div>
    </fieldset>
  );
};

export default OffersSelector;
